"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
import { Loader, Leaf, ArrowRight, CircleCheck } from "lucide-react";
function InquiryFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="147716" className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
                <div className="size-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                    <CircleCheck className="size-8 text-primary" />
                </div>
                <h3 className="text-2xl font-serif font-bold text-foreground">Inquiry Received</h3>
                <p className="text-muted-foreground max-w-sm">{successMessage}</p>
                <Button onClick={resetForm} variant="outline" className="mt-6 rounded-full px-8 border-primary text-primary hover:bg-primary/5">
                    Submit Another Inquiry
                </Button>
            </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Name must be at least 2 characters"
        }
      }} render={({
        field
      }) => <FormItem className="col-span-1 md:col-span-2">
                            <FormLabel className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Your Name</FormLabel>
                            <FormControl>
                                <Input placeholder="Alexander Hamilton" className="bg-background/30 border-0 border-b-2 border-border/30 focus-visible:ring-0 focus-visible:border-primary h-12 rounded-none transition-all duration-300 placeholder:text-muted-foreground/50" {...field} />
                            </FormControl>
                            <FormDescription className="text-[10px] italic">Please enter your full legal or kennel name.</FormDescription>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Email Address</FormLabel>
                            <FormControl>
                                <Input type="email" placeholder="concierge@pupzstudio.com" className="bg-background/30 border-0 border-b-2 border-border/30 focus-visible:ring-0 focus-visible:border-primary h-12 rounded-none transition-all duration-300 placeholder:text-muted-foreground/50" {...field} />
                            </FormControl>
                            <FormDescription className="text-[10px] italic">Our primary channel for exclusive updates.</FormDescription>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Phone Number</FormLabel>
                            <FormControl>
                                <Input type="tel" placeholder="+1 (555) 000-0000" className="bg-background/30 border-0 border-b-2 border-border/30 focus-visible:ring-0 focus-visible:border-primary h-12 rounded-none transition-all duration-300 placeholder:text-muted-foreground/50" {...field} />
                            </FormControl>
                            <FormDescription className="text-[10px] italic">Optional – for direct consultation scheduling.</FormDescription>
                            <FormMessage />
                        </FormItem>} />
            </div>

            <FormField control={control} name="project_description" rules={{
      required: "Project vision is required",
      minLength: {
        value: 10,
        message: "Please provide a bit more detail (min 10 characters)"
      }
    }} render={({
      field
    }) => <FormItem>
                        <FormLabel className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Project Vision</FormLabel>
                        <FormControl>
                            <Textarea placeholder="Describe your kennel's heritage and digital goals..." className="bg-background/30 border-0 border-b-2 border-border/30 focus-visible:ring-0 focus-visible:border-primary min-h-[120px] rounded-none resize-none transition-all duration-300 placeholder:text-muted-foreground/50" {...field} />
                        </FormControl>
                        <FormDescription className="text-[10px] italic">Include your breed focus, aesthetic preferences, and specific service needs.</FormDescription>
                        <FormMessage />
                    </FormItem>} />

            <FormField control={control} name="budget" render={({
      field
    }) => <FormItem>
                        <FormLabel className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Investment Range (USD)</FormLabel>
                        <FormControl>
                            <Input type="number" placeholder="2500" className="bg-background/30 border-0 border-b-2 border-border/30 focus-visible:ring-0 focus-visible:border-primary h-12 rounded-none transition-all duration-300 placeholder:text-muted-foreground/50" {...field} />
                        </FormControl>
                        <FormDescription className="text-[10px] italic">Optional – helps us tailor a suite that matches your legacy goals.</FormDescription>
                        <FormMessage />
                    </FormItem>} />

            {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-xl text-destructive text-sm flex items-start gap-3">
                    <span className="mt-0.5"></span>
                    <p>{submitError}</p>
                </div>}

            <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-lg font-sans font-bold rounded-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-xl shadow-primary/20 hover:shadow-primary/40 transition-all duration-500 group tracking-wider">
                {isSubmitting ? <>
                        <Loader className="animate-spin mr-2 size-5" />
                        Processing Application...
                    </> : <>
                        Request Private Consultation
                        <ArrowRight className="ml-2 size-5 group-hover:translate-x-1 transition-transform" />
                    </>}
            </Button>
        </Form>;
}
export default function Inquiry() {
  const {
    ref: sectionRef,
    isVisible: isSectionVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: formRef,
    isVisible: isFormVisible
  } = useRevealOnScroll({
    threshold: 0.2,
    once: true
  });
  return <section id="serviceapplicationform" className="relative py-24 lg:py-32 bg-background overflow-hidden" ref={sectionRef}>
            {}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
                <div className="absolute -top-[20%] -right-[10%] w-[70%] h-[70%] rounded-full bg-primary/10 blur-[120px] opacity-30" />
                <div className="absolute -bottom-[20%] -left-[10%] w-[60%] h-[60%] rounded-full bg-secondary/10 blur-[120px] opacity-30" />
            </div>

            <div className="container mx-auto px-4 md:px-6 relative z-10">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
                    
                    {}
                    <div className={`lg:col-span-5 flex flex-col justify-center transition-all duration-1000 ease-out ${isSectionVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}>
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-xs font-semibold tracking-widest uppercase w-fit mb-8">
                            <Leaf className="size-4" />
                            <span>The Concierge EXPERIENCE</span>
                        </div>
                        
                        <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-foreground leading-tight tracking-tight mb-8">Ready for a breeder website that reflects the quality of your program?</h2>
                        
                        <p className="text-lg text-muted-foreground mb-10 max-w-md leading-relaxed font-sans">Let’s build a website that helps you look more professional, communicate more clearly, and attract better puppy inquiries.</p>

                        <div className="flex flex-col gap-6 text-sm font-medium text-foreground">
                            <div className="flex items-center gap-3">
                                <div className="size-10 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                                    <CircleCheck className="size-5" />
                                </div>
                                <span className="tracking-wide">Website and consumer engagement Digital Artistry</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="size-10 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                                    <CircleCheck className="size-5" />
                                </div>
                                <span className="tracking-wide">Exclusive Concierge Service</span>
                            </div>
                        </div>
                    </div>

                    {}
                    <div ref={formRef} className={`lg:col-span-7 lg:pl-12 transition-all duration-1000 delay-200 ease-out ${isFormVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
                        <Link to="#" data-slot="card" className="text-card-foreground flex flex-col gap-6 py-6 bg-card/40 backdrop-blur-2xl border-border/20 shadow-2xl shadow-primary/5 rounded-[2rem] overflow-hidden relative">
                            <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-primary via-secondary to-primary opacity-80" />
                            
                            <CardHeader className="pb-6 pt-10 px-10">
                                <CardTitle className="text-3xl font-serif font-bold tracking-tight">Your Details</CardTitle>
                                <CardDescription className="text-base font-sans leading-relaxed">
                                    Complete your inquiry to begin the journey toward a "Best in Show" digital presence.
                                </CardDescription>
                            </CardHeader>
                            
                            <CardContent className="px-10 pb-10">
                                <FormProvider formKey="combined_service_application_form_serviceapplicationandappointmentscheduling" formId="combined_service_application_form_serviceapplicationandappointmentscheduling" sectionName="Inquiry" defaultValues={{
                name: "",
                email: "",
                phone: "",
                project_description: "",
                budget: ""
              }} validationRules={{
                name: {
                  required: true,
                  minLength: 2
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                project_description: {
                  required: true,
                  minLength: 10
                }
              }} submitText="Request Private Consultation" successMessage="Your application has been received by our concierge. We will review your vision and contact you shortly.">
                                    <InquiryFormInner />
                                </FormProvider>
                            </CardContent>
                        </Link>
                    </div>

                </div>
            </div>
        </section>;
}